import Vue from 'vue';
import Vuex from 'vuex';
import uniqid from 'uniqid';

import { STATE_CLASSES } from '@/data/constants/classes';

import {
    SHOW_ALERT,
    HIDE_ALERT,
    SET_ALERT_TEMPLATE,

    SHOW_PROMPT,
    HIDE_PROMPT,
    SET_PROMPT_TEMPLATE,

    SHOW_SNACKBAR,
    HIDE_SNACKBAR,

    FIRE_FLEX_EVENT,
    SET_SETTINGS_DATA,
    SET_PREVIEW_MODE,
    SET_COMPONENT_DATA,
    SET_COORDINATES,
    SET_COLLECTION_DATA,
    CLEAR_COMPONENT_DATA,

    SET_DYNAMIC_MAP_PREVIEW_MODE,
} from '@/store/types/mutation-types';

Vue.use(Vuex);

const store = new Vuex.Store({
    // Global state
    state: {
        alert: null,
        alertTemplates: {},

        prompt: null,
        promptTemplates: {},

        snackbar: {
            title: '',
            text: '',
            closeText: '',
            messages: [],
            persist: false,
        },

        firedFlexEvent: {},
        firedFlexEventCollectionData: {},
        previewMode: true,

        settings: null,
        components: [],
        coordinates: null,
        collectionData: null,

        dynamicMapPreviewMode: false,
    },

    getters: {},

    mutations: {
        /**
         *  Displays the alert modal.
         *
         * Args:
         *      alertData { Object }: The data that will be passed into <alert>.
         *          template { String }: Loads a predefined template. See SET_ALERT_TEMPLATE for more details.
         *          title { String }: The text that will display in the modal header.
         *          description { String }: The text that will display in the modal body.
         *
         * Examples:
         *      // calling w/ hard-coded data
         *      this.showAlert({
         *          title: 'Confirm Action',
         *          description: 'Are you sure you want to continue?',
         *      });
         *
         *      // loading a template
         *      this.showAlert({ template: 'confirm-action' });
         */
        [SHOW_ALERT](state, alertData) {
            const defaultData = {
                title: 'Alert',
            };

            // check if loading a template
            if (alertData.template) {
                const templateData = state.alertTemplates[alertData.template];

                if (templateData) {
                    state.alert = Object.assign(defaultData, templateData, alertData);
                } else {
                    console.warn(`Unable to display the alert. A template with the name "${ alertData.template } could not be found.`);
                }

                return;
            }

            if (!alertData.description) {
                console.warn('Unable to display the alert. One or more required fields are missing.');
                return;
            }

            state.alert = Object.assign(defaultData, alertData);
        },

        [HIDE_ALERT](state) {
            state.alert = null;
        },

        /**
         * Alert templates allow you to load the <alert> component with a set of predefined data.
         *
         * This helps prevent you from typing the same data over and over again if it's used in multiple places.
         *
         * Args:
         *      name { String }: The name of the template you are creating. This should be unique across all templates.
         *      template { Object }: The data that will be passed into <alert>. See SHOW_ALERT for more details.
         *
         * Examples:
         *      this.setAlertTemplate({
         *          name: 'confirm-action',
         *          template: {
         *              title: 'Confirm Action',
         *              description: 'Are you sure you want to continue?',
         *          },
         *      });
         */
        [SET_ALERT_TEMPLATE](state, { name, template }) {
            state.alertTemplates[name] = template;
        },

        /**
         *  Displays the prompt modal.
         *
         * Args:
         *      promptData { Object }: The data that will be passed into <prompt>.
         *          template { String }: Loads a predefined template. See SET_PROMPT_TEMPLATE for more details.
         *          title { String }: The text that will display in the modal header.
         *          description { String }: The text that will display in the modal body.
         *          onContinue { Function }: The function to run if the user continues.
         *          onCancel { Function }: The function to run if the user cancels.
         *
         * Examples:
         *      // calling w/ hard-coded data
         *      this.showPrompt({
         *          title: 'Confirm Action',
         *          description: 'Are you sure you want to continue?',
         *          onContinue: someFunc,
         *          onCancel: anotherFunc,
         *      });
         *
         *      // loading a template
         *      this.showPrompt({ template: 'confirm-action' });
         *
         *      // loading a template w/ overrides
         *      this.showPrompt({
         *          template: 'confirm-action',
         *          onContinue: newFunc,
         *      });
         */
        [SHOW_PROMPT](state, promptData) {
            const defaultData = {
                title: 'Confirmation',
                onCancel: () => {},
            };

            // check if loading a template
            if (promptData.template) {
                const templateData = state.promptTemplates[promptData.template];

                if (templateData) {
                    state.prompt = Object.assign(defaultData, templateData, promptData);
                } else {
                    console.warn(`Unable to display the prompt. A template with the name "${ promptData.template } could not be found.`);
                }

                return;
            }

            if (!promptData.description || !promptData.onContinue) {
                console.warn('Unable to display the prompt. One or more required fields are missing.');
                return;
            }

            state.prompt = Object.assign(defaultData, promptData);
        },

        [HIDE_PROMPT](state) {
            state.prompt = null;
        },

        /**
         * Prompt templates allow you to load the <prompt> component with a set (or subset) of predefined data.
         *
         * This helps prevent you from typing the same data over and over again if it's used in multiple places.
         *
         * Args:
         *      name { String }: The name of the template you are creating. This should be unique across all templates.
         *      template { Object }: The data that will be passed into <prompt>. See SHOW_PROMPT for more details.
         *
         * Examples:
         *      this.setPromptTemplate({
         *          name: 'confirm-action',
         *          template: {
         *              title: 'Confirm Action',
         *              description: 'Are you sure you want to continue?',
         *              onContinue: someFunc,
         *              onCancel: anotherFunc,
         *          },
         *      });
         */
        [SET_PROMPT_TEMPLATE](state, { name, template }) {
            state.promptTemplates[name] = template;
        },

        [SHOW_SNACKBAR](state, {
            title, text, closeText, messages, persist,
        }) {
            state.snackbar = { title, text, closeText, messages, persist };
        },

        [HIDE_SNACKBAR](state) {
            state.snackbar = {
                title: '',
                text: '',
                closeText: '',
            };
        },

        [SET_PREVIEW_MODE](state, isPreview) {
            if (isPreview) {
                document.body.classList.add(STATE_CLASSES.PREVIEW);
                document.body.classList.remove(STATE_CLASSES.EDITING);
            } else {
                document.body.classList.add(STATE_CLASSES.EDITING);
                document.body.classList.remove(STATE_CLASSES.PREVIEW);
            }

            state.previewMode = isPreview;
        },

        [SET_SETTINGS_DATA](state, newSettings) {
            state.settings = Object.assign({}, state.settings, newSettings);
        },

        [FIRE_FLEX_EVENT](state, eventName) {
            state.firedFlexEvent = {
                id: uniqid(), // needed so the reactivity system always gets triggered
                name: eventName,
            };
        },

        [SET_COORDINATES](state, coordinates) {
            state.coordinates = coordinates;
        },

        [SET_COLLECTION_DATA](state, collectionData) {
            state.collectionData = collectionData;
        },

        [SET_DYNAMIC_MAP_PREVIEW_MODE](state, isPreview) {
            state.dynamicMapPreviewMode = isPreview;
        },

        // cache component data by id
        [SET_COMPONENT_DATA](state, componentData) {
            const index = state.components.findIndex(i => i.id === componentData.id);
            if (index > -1) {
                state.components.splice(index, 1, componentData);
            } else {
                state.components.push(componentData);
            }
        },

        // clear cached data
        [CLEAR_COMPONENT_DATA](state) {
            state.components = [];
        },
    },
});

export default store;
